/**
 * @module ForceRecomputeDialog
 * @purpose Thin wrapper around ConfirmDialog supplying reasoning-specific i18n labels,
 *   in one of three honest variants (FR5): a fresh case, a completed run being forced,
 *   or a failed run being retried.
 * @depends ConfirmDialog, react-i18next
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure display — no data fetching
 * - State: none — controlled by caller
 * - Errors: n/a
 */

import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'

export type ForceRecomputeDialogVariant = 'firstRun' | 'force' | 'retry'

interface ForceRecomputeDialogProps {
  open: boolean
  variant: ForceRecomputeDialogVariant
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Variants that replace or discard existing results, so backdrop-dismiss is
 * disabled and the confirm action reads as consequential. `firstRun` starts
 * from nothing, so it is not destructive (A3-class judgement, recorded here).
 */
const DESTRUCTIVE_VARIANTS: ReadonlySet<ForceRecomputeDialogVariant> = new Set(['force', 'retry'])

/**
 * Confirmation dialog for launching a reasoning run — every launch is confirmed,
 * so this dialog is the sole gate between a `request()` call and a POST.
 *
 * @behaviour Derives all four labels mechanically from `reasoning.launch.<variant>.*`
 *            — no hand-written per-variant label map. `destructive` follows
 *            {@link DESTRUCTIVE_VARIANTS}.
 * @errors n/a — purely presentational
 */
export function ForceRecomputeDialog({
  open,
  variant,
  onConfirm,
  onCancel,
}: ForceRecomputeDialogProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <ConfirmDialog
      open={open}
      title={t(`reasoning.launch.${variant}.title`)}
      body={t(`reasoning.launch.${variant}.body`)}
      confirmLabel={t(`reasoning.launch.${variant}.confirm`)}
      cancelLabel={t(`reasoning.launch.${variant}.cancel`)}
      onConfirm={onConfirm}
      onCancel={onCancel}
      destructive={DESTRUCTIVE_VARIANTS.has(variant)}
    />
  )
}
